import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../lib/api";
import {
  LayoutDashboard, Building2, Users, BookOpen, Route, Shield, ClipboardCheck,
  Calendar, BarChart3, Settings, LogOut, ChevronDown, ChevronLeft, ChevronRight,
  DollarSign, Bell, Search, Plus, Megaphone, Sun, Moon, Menu, X, Play,
  GraduationCap, Sparkles, Award, Trophy, FileText, AlertTriangle, ScrollText,
  CreditCard, UserCog, Lock, Palette, Link2, Send, TrendingUp,
  HelpCircle, Keyboard, ExternalLink, UserPlus, Zap, HardDrive, Wrench,
  Inbox, MessageCircle, Key, Clock, Activity, Layers, Package, Receipt,
  ShieldCheck, Cog, Database
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionsContext";
import NotificationBell from "../shared/NotificationBell";
import AccessibilityToolbar from "../shared/AccessibilityToolbar";
import { useBranding } from "../../context/BrandingContext";
import Logo from "../shared/Logo";
import SidebarBackground from "../shared/SidebarBackground";
import { NotificationCenter } from "../shared/NotificationCenter";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";

// ── Navigation configs ──
const getSuperAdminNav = () => ({
  home: {
    label: "Home", icon: LayoutDashboard,
    items: [
      { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { href: "/action-center", label: "Action Center", icon: ClipboardCheck, badgeKey: "total" },
      { href: "/admin/announcements", label: "Announcements", icon: Megaphone },
    ],
  },
  people: {
    label: "Manage", icon: Users,
    items: [
      { href: "/admin/tenants", label: "Tenants", icon: Building2 },
      { href: "/admin/users", label: "Users & Teams", icon: Users, perm: "users.view" },
      { href: "/admin/groups", label: "Groups & Depts", icon: UserPlus, perm: "settings.view" },
      { href: "/admin/support", label: "Support Tickets", icon: HelpCircle, badgeKey: "support_tickets" },
      { href: "/admin/service-requests", label: "Service Requests", icon: Send, badgeKey: "service_requests" },
      { href: "/shared/inbox", label: "Shared Inbox", icon: Inbox },
    ],
  },
  learning: {
    label: "Learning", icon: BookOpen,
    items: [
      { href: "/admin/catalog", label: "Course Catalog", icon: Search, perm: "courses.view" },
      { href: "/admin/courses", label: "Manage Courses", icon: BookOpen, perm: "courses.view" },
      { href: "/admin/learning-paths", label: "Learning Paths", icon: Route, perm: "learning_paths.view" },
      { href: "/admin/sessions", label: "ILT Sessions", icon: Play, perm: "sessions.view" },
      { href: "/admin/calendar", label: "Training Calendar", icon: Calendar, perm: "sessions.view" },
      { href: "/admin/content-library", label: "Content Library", icon: HardDrive, perm: "courses.view" },
      { href: "/admin/certificate-templates", label: "Cert Templates", icon: Award, perm: "courses.view" },
    ],
  },
  analytics: {
    label: "Analytics", icon: BarChart3,
    items: [
      { href: "/admin/analytics", label: "Analytics", icon: BarChart3, perm: "analytics.view" },
      { href: "/admin/executive-analytics", label: "Executive Analytics", icon: TrendingUp, perm: "analytics.view" },
      { href: "/admin/reports", label: "Reports", icon: FileText, perm: "analytics.view" },
      { href: "/admin/scheduled-reports", label: "Scheduled Reports", icon: Clock, perm: "analytics.view" },
    ],
  },
  revenue: {
    label: "Revenue", icon: DollarSign,
    items: [
      { href: "/admin/revenue", label: "Revenue & Accounts", icon: DollarSign, perm: "settings.view" },
      { href: "/admin/invoices", label: "Invoices", icon: Receipt, perm: "settings.view" },
      { href: "/admin/quote-requests", label: "Quote Requests", icon: Send, perm: "sessions.view" },
    ],
  },
  settings: {
    label: "Settings", icon: Cog,
    items: [
      { href: "/admin/settings/branding", label: "Branding", icon: Palette, perm: "settings.view" },
      { href: "/admin/templates", label: "Templates", icon: Layers, perm: "courses.view" },
      { href: "/admin/integrations", label: "Integrations", icon: Link2, perm: "settings.view" },
      { href: "/admin/sso", label: "SSO (SAML)", icon: Key, perm: "settings.view" },
      { href: "/admin/lti", label: "LTI 1.3", icon: ExternalLink, perm: "settings.view" },
      { href: "/admin/embed-engine", label: "Embed Engine", icon: ExternalLink, perm: "settings.view" },
      { href: "/admin/feature-flags", label: "Feature Flags", icon: Zap, perm: "settings.view" },
      { href: "/admin/settings/security", label: "Security", icon: Lock, perm: "settings.view" },
      { href: "/admin/settings/audit", label: "Audit Logs", icon: ScrollText, perm: "settings.view" },
    ],
  },
  system: {
    label: "System", icon: ShieldCheck,
    items: [
      { href: "/admin/platform-staff", label: "Platform Staff", icon: Users, perm: "users.view" },
      { href: "/admin/settings/rbac", label: "Roles & Permissions", icon: ShieldCheck, perm: "settings.view" },
      { href: "/admin/compliance-dashboard", label: "Compliance Dashboard", icon: Activity, perm: "compliance.view" },
      { href: "/admin/test-results-center", label: "Test Results Center", icon: FileText, perm: "compliance.view" },
      { href: "/admin/operations", label: "Operations Center", icon: Zap },
      { href: "/admin/notifications", label: "Notification Config", icon: Bell, perm: "settings.view" },
      { href: "/admin/advanced-tools", label: "Advanced Tools", icon: Wrench, perm: "settings.view" },
      { href: "/system-health", label: "System Health", icon: Activity },
      { href: "/admin/migration", label: "Data Migration", icon: HardDrive, perm: "settings.view" },
      { href: "/admin/error-logs", label: "Error Logs", icon: AlertTriangle },
      { href: "/admin/demo-data", label: "Demo Data Manager", icon: Database },
    ],
  },
});

const getTenantAdminNav = () => ({
  catalogue: {
    label: "Catalogue", icon: Search,
    items: [
      { href: "/tenant/catalogue", label: "Browse Catalogue", icon: Search, perm: "courses.view" },
    ],
  },
  main: {
    label: "Main", icon: LayoutDashboard,
    items: [
      { href: "/tenant/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { href: "/action-center", label: "Action Center", icon: ClipboardCheck, badgeKey: "total" },
      { href: "/tenant/team", label: "Team", icon: Users, perm: "teams.view" },
      { href: "/tenant/access-requests", label: "Access Requests", icon: Send, perm: "enrollments.create" },
    ],
  },
  myLearning: {
    label: "My Learning", icon: Sparkles,
    items: [
      { href: "/my-progress", label: "My Progress", icon: Sparkles },
      { href: "/my-courses", label: "My Courses", icon: GraduationCap },
    ],
  },
  learning: {
    label: "Learning", icon: BookOpen,
    items: [
      { href: "/tenant/courses", label: "Org Courses", icon: BookOpen, perm: "courses.view" },
      { href: "/tenant/learning-paths", label: "Learning Paths", icon: GraduationCap, perm: "learning_paths.view" },
      { href: "/tenant/compliances", label: "Compliances", icon: Shield, perm: "compliance.view" },
      { href: "/tenant/assessments", label: "Assessments", icon: ClipboardCheck, perm: "assessments.view" },
    ],
  },
  operations: {
    label: "Operations", icon: Play,
    items: [
      { href: "/tenant/sessions", label: "ILT Sessions", icon: Play, perm: "sessions.view" },
      { href: "/tenant/calendar", label: "Training Calendar", icon: Calendar, perm: "sessions.view" },
      { href: "/tenant/announcements", label: "Announcements", icon: Megaphone },
    ],
  },
  reports: {
    label: "Reports", icon: BarChart3,
    items: [
      { href: "/tenant/analytics", label: "Analytics", icon: BarChart3, perm: "analytics.view" },
      { href: "/tenant/executive-analytics", label: "Executive", icon: TrendingUp, perm: "analytics.view" },
      { href: "/tenant/risk-assessment", label: "Risk Assessment", icon: AlertTriangle },
      { href: "/tenant/reports", label: "Reports", icon: FileText, perm: "analytics.view" },
    ],
  },
  platform: {
    label: "Platform", icon: ExternalLink,
    items: [
      { href: "/tenant/embed-engine", label: "Embed Engine", icon: ExternalLink, perm: "settings.view" },
      { href: "/admin/sso", label: "SSO (SAML)", icon: Key, perm: "settings.view" },
      { href: "/admin/invoices", label: "Invoices", icon: Receipt, perm: "settings.view" },
      { href: "/admin/scheduled-reports", label: "Scheduled Reports", icon: Clock, perm: "analytics.view" },
      { href: "/admin/certificate-templates", label: "Cert Templates", icon: Award, perm: "settings.view" },
      { href: "/shared/inbox", label: "Shared Inbox", icon: Inbox },
    ],
  },
  settings: {
    label: "Settings", icon: Cog,
    items: [
      { href: "/tenant/settings/profile", label: "Profile", icon: UserCog, perm: "settings.view" },
      { href: "/tenant/settings/account", label: "Organization", icon: Building2, perm: "settings.view" },
      { href: "/tenant/settings/departments", label: "Departments", icon: Users, perm: "settings.view" },
      { href: "/tenant/settings/security", label: "Security", icon: Lock, perm: "settings.view" },
      { href: "/tenant/settings/branding", label: "Branding", icon: Palette, perm: "settings.view" },
      { href: "/tenant/settings/integrations", label: "Integrations", icon: Link2, perm: "settings.view" },
      { href: "/tenant/settings/notifications", label: "Notifications", icon: Bell, perm: "settings.view" },
      { href: "/tenant/settings/audit", label: "Audit Logs", icon: ScrollText, perm: "settings.view" },
      { href: "/tenant/settings/rbac", label: "Roles & Access", icon: Shield, perm: "settings.view" },
      { href: "/system-health", label: "System Health", icon: Activity },
      { href: "/tenant/billing", label: "Billing", icon: CreditCard, perm: "settings.view" },
      { href: "/tenant/support", label: "Support", icon: HelpCircle },
    ],
  },
});

const getLearnerNav = () => ({
  main: {
    label: "Main", icon: LayoutDashboard,
    items: [
      { href: "/learner/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { href: "/action-center", label: "Action Center", icon: ClipboardCheck, badgeKey: "total" },
    ],
  },
  learning: {
    label: "Learning", icon: BookOpen,
    items: [
      { href: "/my-courses", label: "My Courses", icon: BookOpen },
      { href: "/my-progress", label: "My Progress", icon: Sparkles },
      { href: "/learner/learning-paths", label: "Learning Paths", icon: Route },
      { href: "/learner/compliance", label: "My Compliance", icon: Shield },
      { href: "/learner/assessments", label: "Assessments", icon: ClipboardCheck },
      { href: "/learner/certificates", label: "Certificates", icon: Award },
      { href: "/shared/inbox", label: "Shared With Me", icon: Inbox },
    ],
  },
  schedule: {
    label: "Schedule", icon: Calendar,
    items: [
      { href: "/learner/sessions", label: "My Sessions", icon: Calendar },
      { href: "/learner/catalog", label: "Browse Catalog", icon: GraduationCap },
      { href: "/learner/announcements", label: "Announcements", icon: Megaphone },
    ],
  },
  rewards: {
    label: "Rewards", icon: Trophy,
    items: [
      { href: "/learner/gamification", label: "Leaderboard", icon: Trophy },
    ],
  },
  account: {
    label: "Account", icon: Settings,
    items: [
      { href: "/system-health", label: "System Health", icon: Activity },
      { href: "/learner/support", label: "Help & Support", icon: HelpCircle },
      { href: "/learner/settings", label: "Settings", icon: Settings },
    ],
  },
});

const getManagerNav = () => ({
  main: {
    label: "Main", icon: LayoutDashboard,
    items: [
      { href: "/manager/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { href: "/action-center", label: "Action Center", icon: ClipboardCheck, badgeKey: "total" },
    ],
  },
  myLearning: {
    label: "My Learning", icon: BookOpen,
    items: [
      { href: "/my-courses", label: "My Courses", icon: BookOpen },
      { href: "/my-progress", label: "My Progress", icon: Sparkles },
      { href: "/manager/learning-paths", label: "Learning Paths", icon: Route },
      { href: "/manager/compliance", label: "My Compliance", icon: Shield },
      { href: "/manager/certificates", label: "Certificates", icon: Award },
      { href: "/manager/assessments", label: "Assessments", icon: ClipboardCheck },
    ],
  },
  team: {
    label: "Team", icon: Users,
    items: [
      { href: "/manager/team", label: "Team Roster", icon: Users, perm: "teams.view" },
      { href: "/manager/team-enroll", label: "Team Enrollment", icon: UserPlus, perm: "enrollments.create" },
      { href: "/manager/access-requests", label: "Access Requests", icon: Send, perm: "enrollments.create" },
      { href: "/manager/team-compliance", label: "Team Compliance", icon: Shield, perm: "compliance.view" },
      { href: "/manager/team-analytics", label: "Team Analytics", icon: BarChart3, perm: "analytics.view" },
    ],
  },
  schedule: {
    label: "Schedule", icon: Calendar,
    items: [
      { href: "/manager/sessions", label: "My Sessions", icon: Calendar },
      { href: "/manager/catalog", label: "Browse Catalog", icon: GraduationCap },
      { href: "/manager/announcements", label: "Announcements", icon: Megaphone },
    ],
  },
  rewards: {
    label: "Rewards", icon: Trophy,
    items: [
      { href: "/manager/gamification", label: "Leaderboard", icon: Trophy, perm: "gamification.view" },
    ],
  },
  account: {
    label: "Account", icon: Settings,
    items: [
      { href: "/system-health", label: "System Health", icon: Activity },
      { href: "/manager/support", label: "Help & Support", icon: HelpCircle },
      { href: "/manager/settings", label: "Settings", icon: Settings },
    ],
  },
});

// ── HubSpot-style Flyout Panel ──
const FlyoutPanel = ({ group, currentPath, onNavigate }) => {
  const GroupIcon = group.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -6 }}
      transition={{ duration: 0.12 }}
      className="absolute left-full top-0 ml-1 z-[100] min-w-[220px]"
    >
      <div className="bg-[#0F1923] border border-white/10 rounded-xl shadow-2xl py-2 px-1">
        <div className="px-3 py-2 mb-1 flex items-center gap-2">
          <GroupIcon className="w-4 h-4 text-white/50" />
          <span className="text-xs font-bold text-white/50 uppercase tracking-wider">{group.label}</span>
        </div>
        <div className="space-y-0.5">
          {group.items.map(item => {
            const Icon = item.icon;
            const isActive = currentPath === item.href || currentPath.startsWith(item.href + "/");
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150",
                  isActive ? "bg-white/15 text-white font-medium" : "text-white/70 hover:bg-white/10 hover:text-white"
                )}
                data-testid={`flyout-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// ── Sidebar (responsive: overlay on mobile, fixed on desktop) ──
export const AdminSidebar = ({ variant = "super_admin", isCollapsed, onToggleCollapse, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const scrollRef = useRef(null);
  const { hasPermission } = usePermissions();
  const { branding } = useBranding();
  const hasBusinessLogo = !!branding?.main_logo_url;
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const hoverTimeout = useRef(null);
  const [badgeCounts, setBadgeCounts] = useState({});

  // Fetch sidebar badge counts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await api.get("/sidebar-counts");
        setBadgeCounts(res.data || {});
      } catch { /* silent */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  const navConfig = variant === "super_admin" ? getSuperAdminNav()
    : variant === "learner" ? getLearnerNav()
    : variant === "manager" ? getManagerNav()
    : getTenantAdminNav();

  const filterByPerm = (items) => items.filter(item => !item.perm || hasPermission(item.perm));
  const groupEntries = Object.entries(navConfig);

  const getGroupForPath = (path) => {
    for (const [key, group] of groupEntries) {
      if (group.items.some(item => path === item.href || path.startsWith(item.href + "/"))) return key;
    }
    return null;
  };

  const [openGroups, setOpenGroups] = useState(() => {
    const active = getGroupForPath(location.pathname);
    return active ? new Set([active]) : new Set(["main"]);
  });
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      const active = getGroupForPath(location.pathname);
      if (active) {
        setOpenGroups(prev => { const next = new Set(prev); next.add(active); return next; });
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (key) => {
    setOpenGroups(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  const handleGroupMouseEnter = (key) => {
    if (!isCollapsed) return;
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setHoveredGroup(key);
  };

  const handleGroupMouseLeave = () => {
    if (!isCollapsed) return;
    hoverTimeout.current = setTimeout(() => setHoveredGroup(null), 150);
  };

  // ── Render Expanded Groups (for expanded sidebar + mobile) ──
  const renderExpandedGroups = (onNavClick) => (
    <>
      {groupEntries.map(([key, group]) => {
        const filteredItems = filterByPerm(group.items);
        if (filteredItems.length === 0) return null;
        const hasActive = filteredItems.some(item => location.pathname === item.href || location.pathname.startsWith(item.href + "/"));
        const isOpen = openGroups.has(key);
        return (
          <div key={key} className="mb-0.5">
            <button
              onClick={() => toggleGroup(key)}
              className={cn(
                "flex items-center justify-between w-full px-3 py-2 rounded-lg transition-all duration-200",
                hasActive && !isOpen ? "border border-blue-500/30 bg-blue-500/5" : isOpen ? "bg-white/5" : "hover:bg-white/5"
              )}
              data-testid={`navgroup-${key}`}
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-2">
                <group.icon className="w-4 h-4 text-white/50" />
                <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">{group.label}</span>
              </div>
              <ChevronRight className={`w-3 h-3 text-white/30 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.nav
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-0.5 overflow-hidden mt-0.5 ml-1"
                >
                  {filteredItems.map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
                    const badgeCount = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={onNavClick}
                        className={cn(
                          "relative flex items-center gap-2.5 rounded-lg h-9 px-3 transition-all duration-200",
                          isActive ? "bg-white/15 text-white font-medium" : "text-white/70 hover:bg-white/10 hover:text-white"
                        )}
                        data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate text-sm">{item.label}</span>
                        {badgeCount > 0 && (
                          <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid={`badge-${item.badgeKey}`}>
                            {badgeCount > 99 ? "99+" : badgeCount}
                          </span>
                        )}
                        {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-white rounded-r-full" />}
                      </Link>
                    );
                  })}
                </motion.nav>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );

  // ── Render Collapsed Groups (icon strip + flyout on hover) ──
  const renderCollapsedGroups = () => (
    <div className="flex flex-col items-center gap-1 py-1">
      {groupEntries.map(([key, group]) => {
        const filteredItems = filterByPerm(group.items);
        if (filteredItems.length === 0) return null;
        const hasActive = filteredItems.some(item => location.pathname === item.href || location.pathname.startsWith(item.href + "/"));
        const GroupIcon = group.icon;
        return (
          <div
            key={key}
            className="relative"
            onMouseEnter={() => handleGroupMouseEnter(key)}
            onMouseLeave={handleGroupMouseLeave}
          >
            <button
              className={cn(
                "w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 relative",
                hasActive ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
              )}
              data-testid={`collapsed-icon-${key}`}
              aria-label={group.label}
            >
              <GroupIcon className="w-5 h-5" />
              {/* Show red dot if any item in group has badge */}
              {filteredItems.some(i => i.badgeKey && (badgeCounts[i.badgeKey] || 0) > 0) && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#0F1923]" />
              )}
            </button>
            <AnimatePresence>
              {hoveredGroup === key && (
                <FlyoutPanel
                  group={{ ...group, items: filteredItems }}
                  currentPath={location.pathname}
                  onNavigate={() => setHoveredGroup(null)}
                />
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {/* ── Mobile Overlay ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={onMobileClose}
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 h-screen w-[260px] text-white flex flex-col z-50 lg:hidden overflow-hidden"
              style={{ background: "linear-gradient(to bottom, var(--sidebar-bg, #0F1923), color-mix(in srgb, var(--sidebar-bg, #0F1923), #1A2B3C 40%), var(--sidebar-bg, #0F1923))" }}
              role="navigation"
              aria-label="Main navigation"
            >
              <SidebarBackground />
              <div className="relative z-10 h-14 flex items-center justify-between px-4 border-b border-white/10">
                {hasBusinessLogo ? (
                  <Link to="/" className="flex items-center gap-2" aria-label="Home">
                    <img src={branding.main_logo_url} alt="Logo" className="h-[38px] w-auto object-contain" />
                  </Link>
                ) : (
                  <Logo size="sidebar" showText={false} className="text-white" />
                )}
                <button onClick={onMobileClose} className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10" data-testid="mobile-sidebar-close" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ScrollArea className="flex-1 relative z-10 py-3 px-2">
                {renderExpandedGroups(onMobileClose)}
              </ScrollArea>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Desktop Sidebar ── */}
      <motion.aside
        initial={false}
        animate={{ width: isCollapsed ? 60 : 240 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="sticky top-0 h-screen text-white flex-col z-40 overflow-visible hidden lg:flex shrink-0"
        style={{ background: "linear-gradient(to bottom, var(--sidebar-bg, #0F1923), color-mix(in srgb, var(--sidebar-bg, #0F1923), #1A2B3C 40%), var(--sidebar-bg, #0F1923))" }}
        role="navigation"
        aria-label="Main navigation"
      >
        <SidebarBackground />
        {/* Logo */}
        <div className="relative z-10 h-14 flex items-center justify-center px-3 border-b border-white/10">
          {hasBusinessLogo ? (
            <Link to="/" className="flex items-center" aria-label="Home">
              <img src={branding.main_logo_url} alt="Logo" className={`${isCollapsed ? "h-[28px]" : "h-[38px]"} w-auto object-contain transition-all`} />
            </Link>
          ) : (
            <Logo size="sidebar" showText={false} className="text-white" />
          )}
        </div>
        {/* Nav area */}
        {isCollapsed ? (
          <div className="flex-1 relative z-10 py-2 px-1.5 overflow-visible">
            {renderCollapsedGroups()}
          </div>
        ) : (
          <ScrollArea className="flex-1 relative z-10 py-2 px-1.5" ref={scrollRef}>
            {renderExpandedGroups()}
          </ScrollArea>
        )}
        {/* Footer: logo + version + collapse */}
        <div className="relative z-10 border-t border-white/10">
          {hasBusinessLogo && (
            <div className="flex items-center justify-center py-1.5 border-b border-white/5">
              <img src="/odesie-logo-white.png" alt="Powered by Odesie" className={`${isCollapsed ? "h-[16px]" : "h-[20px]"} w-auto opacity-40`} />
            </div>
          )}
          {!isCollapsed && (
            <div className="flex items-center justify-center py-1">
              <span className="text-[10px] text-white/25 font-mono tracking-wide">v0.5 Beta</span>
            </div>
          )}
          <div className="p-2">
            <button
              onClick={onToggleCollapse}
              className="w-full flex items-center justify-center gap-2 px-2 py-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="sidebar-collapse-btn"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : (<><ChevronLeft className="w-4 h-4" /><span className="text-xs">Collapse</span></>)}
            </button>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

// ── Header (HubSpot-style enterprise) ──
export const AdminHeader = ({ title, showSearch = true, onMobileMenuOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const searchRef = useRef(null);
  const searchTimer = useRef(null);

  const handleLogout = () => { logout(); navigate("/auth/login"); };
  const toggleTheme = () => {
    const newIsDark = !isDark;
    if (newIsDark) { document.documentElement.classList.add('dark'); localStorage.setItem('theme', 'dark'); }
    else { document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
    setIsDark(newIsDark);
  };

  const handleSearch = useCallback((q) => {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.length < 2) { setSearchResults([]); setSearchOpen(false); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setSearchResults(res.data.results || []);
        setSearchOpen(true);
      } catch (e) { setSearchResults([]); }
    }, 300);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const SEARCH_ICONS = { course: BookOpen, user: Users, session: Calendar, learning_path: Route, compliance: Shield };
  const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U";
  const roleName = user?.role === "tenant_admin" ? "Business Admin" : user?.role === "super_admin" ? "Super Admin" : user?.role === "learner" ? "Learner" : user?.role === "manager" ? "Manager" : (user?.role || "User").replace("_", " ");
  const isLearner = user?.role === "learner";
  const isManager = user?.role === "manager";
  const profileLink = isLearner ? "/learner/settings" : isManager ? "/manager/settings" : "/admin/settings/profile";
  const settingsLink = isLearner ? "/learner/settings" : isManager ? "/manager/settings" : "/admin/revenue";

  return (
    <TooltipProvider>
    <motion.header initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border" role="banner" aria-label="Page header">
      <div className="flex items-center justify-between h-12 px-3 sm:px-4 lg:px-6">
        {/* Left: Hamburger + Title */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            onClick={onMobileMenuOpen}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted lg:hidden"
            data-testid="mobile-menu-btn"
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" aria-hidden="true" />
          </button>
          {title && (
            <h1 className="text-sm font-semibold text-foreground truncate hidden sm:block" aria-live="polite">
              {title}
            </h1>
          )}
        </div>

        {/* Center: Global Search */}
        {showSearch && (
          <div ref={searchRef} className="hidden sm:block relative max-w-md flex-1 mx-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
              <Input
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search Odesie..."
                className="pl-8 pr-14 h-8 text-sm bg-muted/50 border-border/60 rounded-lg focus:bg-background focus:ring-1 focus:ring-primary"
                aria-label="Global search"
                data-testid="global-search-input"
              />
              <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] bg-background border px-1.5 py-0.5 rounded font-mono text-muted-foreground" aria-hidden="true">
                Ctrl K
              </kbd>
            </div>
            {searchOpen && searchResults.length > 0 && (
              <div className="absolute top-full left-0 w-full mt-1 bg-card border border-border rounded-xl shadow-xl max-h-80 overflow-y-auto z-50">
                {searchResults.map((result, idx) => {
                  const Icon = SEARCH_ICONS[result.type] || FileText;
                  return (
                    <Link
                      key={idx}
                      to={result.url || "#"}
                      onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors"
                      data-testid={`search-result-${idx}`}
                    >
                      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{result.title || result.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{result.type}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={toggleTheme}
                className="hidden md:inline-flex p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                data-testid="theme-toggle"
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>{isDark ? "Light mode" : "Dark mode"}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate("/keyboard-shortcuts")}
                className="hidden md:inline-flex p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Keyboard shortcuts"
                data-testid="shortcuts-btn"
              >
                <Keyboard className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Keyboard shortcuts</TooltipContent>
          </Tooltip>

          <NotificationBell />

          <AccessibilityToolbar />

          {/* Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors" data-testid="user-profile-dropdown" aria-label="User menu">
                <Avatar className="w-7 h-7">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden lg:block text-sm font-medium truncate max-w-[100px]">{user?.full_name}</span>
                <ChevronDown className="hidden lg:block w-3 h-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-0 rounded-xl" sideOffset={8}>
              <div className="px-4 py-3 border-b">
                <p className="font-semibold text-sm">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <Badge variant="secondary" className="mt-1 text-[10px] px-2 py-0.5">{roleName}</Badge>
              </div>

              <div className="py-1">
                <DropdownMenuItem onClick={() => navigate(profileLink)} className="flex items-center gap-2 py-2 px-4 cursor-pointer" data-testid="profile-link">
                  <UserCog className="w-4 h-4 text-muted-foreground" /> Profile Settings
                </DropdownMenuItem>
                {!isLearner && (
                  <DropdownMenuItem onClick={() => navigate(settingsLink)} className="flex items-center gap-2 py-2 px-4 cursor-pointer" data-testid="settings-link">
                    <Settings className="w-4 h-4 text-muted-foreground" /> App Settings
                  </DropdownMenuItem>
                )}
              </div>

              <DropdownMenuSeparator className="my-0" />

              <div className="py-1">
                <DropdownMenuItem onClick={() => navigate("/keyboard-shortcuts")} className="flex items-center gap-2 py-2 px-4 cursor-pointer" data-testid="dropdown-keyboard-shortcuts">
                  <Keyboard className="w-4 h-4 text-muted-foreground" /> Keyboard Shortcuts
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/product-updates")} className="flex items-center gap-2 py-2 px-4 cursor-pointer" data-testid="dropdown-product-updates">
                  <Sparkles className="w-4 h-4 text-muted-foreground" /> Product Updates
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/training-docs")} className="flex items-center gap-2 py-2 px-4 cursor-pointer" data-testid="dropdown-training-docs">
                  <GraduationCap className="w-4 h-4 text-muted-foreground" /> Training Docs
                </DropdownMenuItem>
              </div>

              <DropdownMenuSeparator className="my-0" />

              <div className="flex items-center justify-between px-4 py-2">
                <button onClick={handleLogout} className="text-sm text-primary hover:underline font-medium" data-testid="logout-btn">Sign out</button>
                <Link to="/privacy" className="text-[11px] text-muted-foreground hover:underline">Privacy policy</Link>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.header>
    </TooltipProvider>
  );
};

// ── AdminLayout (responsive: no sidebar margin on mobile) ──
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";

export const AdminLayout = ({ children, title, variant = "super_admin" }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  const resolvedVariant = (() => {
    if (!user?.role) return variant;
    const roleToVariant = { super_admin: "super_admin", tenant_admin: "tenant_admin", manager: "manager", learner: "learner" };
    return roleToVariant[user.role] || variant;
  })();

  useKeyboardShortcuts({
    onToggleSidebar: () => setSidebarCollapsed(prev => !prev),
    onToggleNotifications: () => {
      const btn = document.querySelector('[data-testid="notification-bell"]');
      btn?.click();
    },
  });

  useEffect(() => {
    document.title = title ? `${title} | Odesie` : "Odesie - Enterprise LMS";
  }, [title]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <a href="#main-content" className="skip-nav" data-testid="skip-nav">
        Skip to main content
      </a>
      <div className="flex flex-1">
        <AdminSidebar
          variant={resolvedVariant}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
        <div className="flex flex-col flex-1 min-w-0">
          <AdminHeader title={title} onMobileMenuOpen={() => setMobileOpen(true)} />
          <main id="main-content" role="main" className="p-3 sm:p-4 lg:p-6 flex-1" aria-label={title || "Page content"} tabIndex={-1}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              {children}
            </motion.div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
